import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { LoaderSymbol } from '../../components/loading';
import { FormTitle } from '../../components/FormTitle';
import { NavIcon } from '../../components/NavIcon';
import { navTree, type NavGroupChild } from '../../layouts/navData';
import { PROTECTED_FROM_HIDE } from '../../layouts/navPermissions';
import '../backlog/backlog.css';

/**
 * Settings → Form Config.  The single admin page that owns the GLOBAL
 * visibility switch for every form in Alpaca Intelligence.
 *
 * <para>Visibility is organised <b>by module</b> (the sidebar nav groups).
 * Each module has a master toggle: switching a module OFF hides every form in
 * it; switching it ON shows them all.  Individual forms can still be toggled
 * one-by-one by expanding the module.  Only forms set to <b>visible</b> are
 * eligible to show — the effective set a user sees at login is
 * <c>(globally visible) AND (already permitted to that user)</c>.  This page
 * owns the first half; the per-user half stays in the role/permission tables.</para>
 *
 * Storage is a server-side JSON file (no DB change) read/written via
 * <c>/api/v1/admin/form-visibility</c> (service/admin only).  A single form —
 * Form Config itself — is locked ON so an admin can never lock themselves out.
 */

interface CatalogRow { id: string; label: string }
interface CatalogSection {
  /** Stable key — the nav group id (or 'overview'). */
  key: string;
  label: string;
  icon: string;
  rows: CatalogRow[];
}

/** Full form catalog, grouped + ordered exactly like the sidebar.  Built once
 *  from the static nav tree — each top-level group becomes one "module"
 *  section, and loose top-level items are placed in whichever
 *  `{ kind: 'section' }` marker most recently preceded them (so Home / AVA /
 *  Executive Dashboard end up in "Overview", Server Monitor ends up in
 *  "System", Settings items in "Settings", and so on).  Items appearing
 *  before any section marker fall into a default "Overview" section. */
const FORM_CATALOG: CatalogSection[] = (() => {
  const sections: CatalogSection[] = [];
  const seen = new Set<string>();
  // Use a slugified section label as the stable key.
  const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const iconForSection = (label: string): string => {
    const l = label.toLowerCase();
    if (l === 'overview') return 'ti-layout-grid';
    if (l === 'system')   return 'ti-server';
    if (l === 'settings') return 'ti-settings';
    return 'ti-circles';
  };
  let currentLoose: CatalogSection = { key: 'overview', label: 'Overview', icon: 'ti-layout-grid', rows: [] };
  const looseList: CatalogSection[] = [currentLoose];

  const push = (section: CatalogSection, id: string, label: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    section.rows.push({ id, label });
  };

  // Recursively pull every clickable item out of a group's nested `children`
  // tree (sub-groups nest arbitrarily deep).  This is what makes Form Config
  // AUTOMATIC: any new form added to the nav — whether to a group's flat
  // `items` OR to a nested sub-group — is captured here with no extra wiring.
  // `subhead` markers carry no id, so they're skipped; `push` dedupes by id,
  // so a form that appears in both `items` and `children` is listed once.
  const collectChildren = (children: NavGroupChild[], section: CatalogSection) => {
    for (const c of children) {
      if (c.kind === 'item') push(section, c.item.id, c.item.label);
      else if (c.kind === 'subgroup') collectChildren(c.subgroup.children, section);
    }
  };

  for (const e of navTree) {
    if (e.kind === 'section') {
      // Reuse an existing section by slug, otherwise start a new one.
      const key = slug(e.label) || 'overview';
      let sec = looseList.find(s => s.key === key);
      if (!sec) {
        sec = { key, label: e.label, icon: iconForSection(e.label), rows: [] };
        looseList.push(sec);
      }
      currentLoose = sec;
    }
    if (e.kind === 'item') push(currentLoose, e.item.id, e.item.label);
    if (e.kind === 'group') {
      const sec: CatalogSection = { key: e.group.id, label: e.group.label, icon: e.group.icon, rows: [] };
      for (const it of e.group.items) push(sec, it.id, it.label);
      if (e.group.children) collectChildren(e.group.children, sec);   // nested sub-group forms
      if (sec.rows.length) sections.push(sec);
    }
  }
  // Loose sections first (Overview, Settings, System), then nav groups in order.
  return [...looseList.filter(s => s.rows.length), ...sections];
})();

const ALL_IDS: string[] = FORM_CATALOG.flatMap((s) => s.rows.map((r) => r.id));
const TOTAL = ALL_IDS.length;

/** A form mapped to `false` on the server is hidden; anything else is visible. */
function buildWorking(serverMap: Record<string, boolean>): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const id of ALL_IDS) {
    out[id] = PROTECTED_FROM_HIDE.has(id) ? true : serverMap[id] !== false;
  }
  return out;
}

type TriState = 'all' | 'none' | 'some';

/** Small on/off switch — a real checkbox styled as a pill toggle. */
function Toggle({ on, locked, onChange }: { on: boolean; locked?: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={locked}
      onClick={onChange}
      title={locked ? 'Always on — cannot be hidden' : on ? 'Visible' : 'Hidden'}
      style={{
        position: 'relative', width: 38, height: 21, borderRadius: 999, border: 'none',
        padding: 0, flexShrink: 0,
        cursor: locked ? 'not-allowed' : 'pointer',
        background: on ? 'var(--brand-accent, var(--ai-orange))' : 'var(--ai-line)',
        opacity: locked ? 0.55 : 1,
        transition: 'background .15s',
      }}
    >
      <span
        style={{
          position: 'absolute', top: 2, left: on ? 19 : 2, width: 17, height: 17,
          borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.3)',
          transition: 'left .15s',
        }}
      />
    </button>
  );
}

/** Larger 3-state module master switch.  `all` = orange right, `none` = grey
 *  left, `some` = amber middle (mixed). */
function ModuleToggle({ state, disabled, onChange }: { state: TriState; disabled?: boolean; onChange: () => void }) {
  const bg = state === 'all' ? 'var(--brand-accent, var(--ai-orange))' : state === 'some' ? 'var(--ai-required-line)' : 'var(--ai-line)';
  const left = state === 'all' ? 27 : state === 'some' ? 14.5 : 3;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={state === 'all'}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      title={disabled ? 'Read-only — press Edit to change' : state === 'all' ? 'All visible — click to hide module' : state === 'none' ? 'All hidden — click to show module' : 'Some hidden — click to show all'}
      style={{
        position: 'relative', width: 50, height: 26, borderRadius: 999, border: 'none',
        padding: 0, flexShrink: 0, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        background: bg, transition: 'background .15s',
      }}
    >
      <span
        style={{
          position: 'absolute', top: 3, left, width: 20, height: 20,
          borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.35)',
          transition: 'left .15s',
        }}
      />
    </button>
  );
}

export default function FormVisibilityPage() {
  const notify = useNotify();
  // View-then-Edit gate (BO UI Standard v2): the panel opens READ-ONLY; the
  // toolbar Edit button unlocks the controls; Save persists through the EXISTING
  // save logic (shared useSaveFlow via gate.save); Cancel reverts to the server
  // snapshot.  `ro` (read-only) OR-combines into every editable control.
  const gate = useEditGate();
  const ro = gate.readOnly;
  const [working, setWorking] = useState<Record<string, boolean>>(() => buildWorking({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** Persistent load-failure message — shown inline because the page cannot
   *  function until the initial fetch succeeds. Save outcomes go through the
   *  shared notification service, not this banner. */
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // Modules start collapsed for a clean overview — the master toggles are the
  // primary control; expand a module to fine-tune individual forms.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  /** Load the live visibility map from the server — reused by initial mount,
   *  the toolbar Refresh, and Cancel (revert = reload from server). */
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/v1/admin/form-visibility');
      const map = (data?.data?.forms ?? {}) as Record<string, boolean>;
      setWorking(buildWorking(map));
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 403
        ? 'Service account access required to manage form visibility.'
        : 'Could not load form visibility settings.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const visibleCount = useMemo(
    () => ALL_IDS.reduce((n, id) => (working[id] ? n + 1 : n), 0),
    [working],
  );

  const toggle = (id: string) => {
    if (ro || PROTECTED_FROM_HIDE.has(id)) return;
    setWorking((w) => ({ ...w, [id]: !w[id] }));
  };

  /** Cascade a value to every (non-protected) form in a module. */
  const setSection = (rows: CatalogRow[], value: boolean) => {
    if (ro) return;
    setWorking((w) => {
      const out = { ...w };
      for (const r of rows) out[r.id] = PROTECTED_FROM_HIDE.has(r.id) ? true : value;
      return out;
    });
  };

  const setAll = (value: boolean) => {
    if (ro) return;
    setWorking(() => {
      const out: Record<string, boolean> = {};
      for (const id of ALL_IDS) out[id] = PROTECTED_FROM_HIDE.has(id) ? true : value;
      return out;
    });
  };

  /** Persist through the EXISTING PUT + re-snapshot the returned map. */
  const persistAll = async () => {
    const { data } = await api.put('/api/v1/admin/form-visibility', { forms: working });
    const map = (data?.data?.forms ?? working) as Record<string, boolean>;
    setWorking(buildWorking(map));
  };

  /** Toolbar Save — the shared view-then-edit save (confirm → audited PUT →
   *  "Saved successfully." → back to read-only). Visibility applies on each
   *  user's next sign-in (no list view; stay on the page). */
  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'Form Config',
      successMessage: "Saved successfully. Visibility changes take effect on each user's next sign-in (or when their permissions refresh).",
      save: persistAll,
      reload: load,
      onError: (e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        notify.error(status === 403
          ? 'Service account access required to save form visibility.'
          : 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  /** Cancel — discard edits by reloading the server snapshot, leave edit mode. */
  const handleCancel = () => gate.cancel(() => void load());

  const q = query.trim().toLowerCase();
  const sections = useMemo(() => {
    if (!q) return FORM_CATALOG;
    return FORM_CATALOG
      .map((s) => ({
        ...s,
        rows: s.label.toLowerCase().includes(q)
          ? s.rows
          : s.rows.filter((r) => r.label.toLowerCase().includes(q)),
      }))
      .filter((s) => s.rows.length > 0);
  }, [q]);

  /** Count visible forms in a module + derive its master tri-state. */
  const sectionState = (rows: CatalogRow[]): { on: number; total: number; state: TriState } => {
    let on = 0;
    for (const r of rows) if (working[r.id]) on++;
    const total = rows.length;
    const state: TriState = on === 0 ? 'none' : on === total ? 'all' : 'some';
    return { on, total, state };
  };

  const isOpen = (key: string) => (q ? true : !!expanded[key]);
  const toggleOpen = (key: string) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  return (
    <div className="bl-page">
      {/* Fixed top */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Form Config</span>
          </div>
          <div className="dp-title-row"><FormTitle pageId="formsetup" loading={loading} auditFormName="Form Config" /></div>
          <div className="dp-subtitle">
            <i className="ti ti-forms" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Global form visibility by module · a form shows only when its module is visible here AND it's permitted for the user
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bl-grid-area">
        {/* Standard action bar (§18.6): view-then-Edit (shared EditGateActions —
            ✎ Edit → 💾 Save / ↩︎ Cancel) · Refresh.  The page is READ-ONLY until
            Edit; Save persists through the existing PUT in one audited batch.
            Bulk "Show all / Hide all" are edit-only form mutators, so they sit
            in the toolbar `left` slot after the gate actions and are gated by ro. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                <EditGateActions
                  gate={gate}
                  saving={saving}
                  disabled={loading}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
                <button type="button" className="ca-btn" onClick={() => setAll(true)} disabled={ro || loading || saving}>Show all</button>
                <button type="button" className="ca-btn" onClick={() => setAll(false)} disabled={ro || loading || saving}>Hide all</button>
              </>
            }
            onRefresh={() => void load()}
            refreshing={loading}
            refreshDisabled={gate.editing}
          />
        </div>

        <div className="bl-card bl-card-flex">
          <div className="section-hdr" style={{ flexWrap: 'wrap', gap: 10 }}>
            <div className="section-title"><i className="ti ti-forms" /> Form Config ({visibleCount}/{TOTAL} visible)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
              <div className="list-search" style={{ width: 240, padding: '5px 10px' }}>
                <span className="tb-i">🔍</span>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search modules or forms…"
                />
                {query && <span className="list-search-clear" onClick={() => setQuery('')}>×</span>}
              </div>
            </div>
          </div>

          {error && (
            <div style={{ margin: '10px 20px 0', padding: '8px 12px', borderRadius: 6,
              background: 'var(--ai-pill-alert-bg)', border: '1px solid var(--ai-pill-alert-fg)', color: 'var(--ai-red)', fontSize: 13 }}>
              <i className="ti ti-alert-circle" style={{ marginRight: 6, verticalAlign: 'middle' }} />{error}
            </div>
          )}

          <div style={{ overflowY: 'auto', padding: '12px 20px 18px' }}>
            {loading ? (
              <div style={{ padding: 24, color: 'var(--ai-muted)', fontSize: 13 }}>
                <LoaderSymbol size={14} /> Loading…
              </div>
            ) : sections.length === 0 ? (
              <div style={{ padding: 24, color: 'var(--ai-muted)', fontSize: 13 }}>No modules or forms match “{query}”.</div>
            ) : (
              sections.map((sec) => {
                const { on, total, state } = sectionState(sec.rows);
                const open = isOpen(sec.key);
                const moduleHidden = state === 'none';
                return (
                  <div key={sec.key} style={{
                    marginBottom: 12, border: '1px solid var(--ai-line)', borderRadius: 9,
                    overflow: 'hidden', background: '#fff',
                  }}>
                    {/* Module header */}
                    <div
                      onClick={() => toggleOpen(sec.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer',
                        padding: '11px 14px',
                        background: moduleHidden ? 'var(--ai-header-bg)' : 'var(--ai-header-bg)',
                        borderBottom: open ? '1px solid var(--ai-line-soft)' : 'none',
                      }}
                    >
                      <i className={`ti ti-chevron-right`} style={{
                        fontSize: 15, color: 'var(--ai-muted)', flexShrink: 0,
                        transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s',
                      }} />
                      <span style={{
                        width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: state === 'all' ? 'var(--ai-orange-tint)' : state === 'some' ? 'var(--ai-orange-tint)' : 'var(--ai-line-soft)',
                        color: state === 'none' ? 'var(--ai-muted)' : 'var(--brand-accent, var(--ai-orange))',
                      }}>
                        <NavIcon icon={sec.icon} />
                      </span>
                      <span style={{
                        fontSize: 14, fontWeight: 700,
                        color: moduleHidden ? 'var(--ai-muted)' : 'var(--ai-ink)',
                      }}>{sec.label}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
                        background: state === 'none' ? 'var(--ai-pill-neutral-bg)' : 'var(--ai-pill-warn-bg)',
                        color: state === 'none' ? 'var(--ai-pill-neutral-fg)' : 'var(--ai-pill-warn-fg)',
                      }}>{on}/{total} visible</span>
                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11.5, color: 'var(--ai-muted)', fontWeight: 600 }}>
                          {state === 'all' ? 'Module on' : state === 'none' ? 'Module off' : 'Mixed'}
                        </span>
                        <ModuleToggle
                          state={state}
                          disabled={ro}
                          onChange={() => setSection(sec.rows, state !== 'all')}
                        />
                      </div>
                    </div>

                    {/* Module body — individual forms */}
                    {open && (
                      <div style={{ padding: '4px 14px 8px 50px' }}>
                        {sec.rows.map((r) => {
                          const locked = PROTECTED_FROM_HIDE.has(r.id);
                          const on = working[r.id] ?? true;
                          return (
                            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12,
                              padding: '6px 2px', borderBottom: '1px solid var(--ai-line-soft)' }}>
                              <Toggle on={on} locked={locked || ro} onChange={() => toggle(r.id)} />
                              <span style={{ fontSize: 13, color: on ? 'var(--ai-ink)' : 'var(--ai-muted)' }}>{r.label}</span>
                              {locked && (
                                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ai-muted)',
                                  border: '1px solid var(--ai-line)', borderRadius: 4, padding: '1px 5px' }}>Always on</span>
                              )}
                              <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ai-muted)', fontFamily: 'var(--ai-font-mono)' }}>{r.id}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
